import { Component } from '@theme/component';

/**
 * @typedef {Object} TabsRefs
 * @property {HTMLButtonElement[]} [tabs] - The tab buttons
 * @property {HTMLElement[]} [panels] - The panels, in the same order as the tabs
 */

/**
 * A set of tabs. One panel is shown at a time and the tab buttons act as disclosure buttons.
 *
 * @extends {Component<TabsRefs>}
 */
class TabsComponent extends Component {
  #selectedIndex = 0;

  #controller = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;

    this.addEventListener('shopify:block:select', this.#handleBlockSelect, { signal });
    this.#update();
    this.dataset.ready = 'true';
  }

  updatedCallback() {
    super.updatedCallback();
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Selects the tab that was clicked.
   * @param {Event} event
   */
  select(event) {
    // The framework routes events from the document, so `target` is the element with the `on:click` attribute
    const tab = event.target;
    const index = this.#tabs.findIndex((item) => item === tab);

    if (index === -1) return;

    this.#selectedIndex = index;
    this.#update();
  }

  /**
   * Shows the panel of the block selected in the theme editor.
   * @param {Event} event
   */
  #handleBlockSelect = (event) => {
    const index = this.#panels.findIndex((panel) => panel === event.target || panel.contains(/** @type {Node} */ (event.target)));

    if (index === -1) return;

    this.#selectedIndex = index;
    this.#update();
  };

  get #tabs() {
    return Array.isArray(this.refs.tabs) ? this.refs.tabs : [];
  }

  get #panels() {
    return Array.isArray(this.refs.panels) ? this.refs.panels : [];
  }

  #update() {
    const tabs = this.#tabs;
    const panels = this.#panels;

    if (this.#selectedIndex >= tabs.length) this.#selectedIndex = 0;

    tabs.forEach((tab, index) => {
      tab.setAttribute('aria-expanded', String(index === this.#selectedIndex));
    });

    panels.forEach((panel, index) => {
      panel.hidden = index !== this.#selectedIndex;
    });
  }
}

if (!customElements.get('tabs-component')) {
  customElements.define('tabs-component', TabsComponent);
}
